/**
 * Binom Advanced sub-tab — Binom Protect (VPN / Bot detection),
 * Campaign settings (payout / postback / traffic loss percents), Notes.
 *
 * Mirrors the right-side advanced area of the Binom panel's Campaign Edit
 * page. Edits go through draft.campaignMeta same as the Campaign tab.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { Switch } from '@/components/ui/form/controls/Switch';

import type {
  BinomCampaignMetaDraft,
  BinomFlowDraft,
} from '../../binom-flow-draft.types';

import BinomCampaignFormRow from '../campaign/BinomCampaignFormRow';
import BinomIntegrationsSection from './BinomIntegrationsSection';
import BinomPercentInput from './BinomPercentInput';
import BinomUsersSection from './BinomUsersSection';

export interface BinomAdvancedConfigTabProps {
  draft: BinomFlowDraft;
  patchCampaignMeta: (m: (d: BinomCampaignMetaDraft) => void) => void;
  campaignId: string | undefined;
}

const BinomAdvancedConfigTab: FC<BinomAdvancedConfigTabProps> = ({
  draft,
  patchCampaignMeta,
  campaignId,
}) => {
  const meta = draft.campaignMeta;
  const settings = meta.campaignSettings ?? {};
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-2">
      <section className="space-y-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Binom Protect
        </h4>
        <BinomCampaignFormRow label="VPN / Proxy">
          <Switch
            checked={Boolean(meta.vpnProxyDetection)}
            onCheckedChange={(v) =>
              patchCampaignMeta((d) => (d.vpnProxyDetection = Boolean(v)))
            }
            aria-label="VPN / Proxy detection"
          />
        </BinomCampaignFormRow>
        <BinomCampaignFormRow label="Bot detection">
          <Switch
            checked={Boolean(meta.botDetection)}
            onCheckedChange={(v) =>
              patchCampaignMeta((d) => (d.botDetection = Boolean(v)))
            }
            aria-label="Bot detection"
          />
        </BinomCampaignFormRow>
      </section>

      <section className="space-y-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Campaign settings
        </h4>
        <BinomCampaignFormRow label="Payout %">
          <BinomPercentInput
            value={settings.payoutPercent}
            onChange={(v) =>
              patchCampaignMeta((d) => {
                d.campaignSettings = { ...d.campaignSettings, payoutPercent: v };
              })
            }
            ariaLabel="Payout percent"
          />
        </BinomCampaignFormRow>
        <BinomCampaignFormRow label="Postback %">
          <BinomPercentInput
            value={settings.postbackPercent}
            onChange={(v) =>
              patchCampaignMeta((d) => {
                d.campaignSettings = { ...d.campaignSettings, postbackPercent: v };
              })
            }
            ariaLabel="Postback percent"
          />
        </BinomCampaignFormRow>
        <BinomCampaignFormRow label="Traffic loss %">
          <BinomPercentInput
            value={settings.trafficLossPercent}
            onChange={(v) =>
              patchCampaignMeta((d) => {
                d.campaignSettings = { ...d.campaignSettings, trafficLossPercent: v };
              })
            }
            ariaLabel="Traffic loss percent"
          />
        </BinomCampaignFormRow>
      </section>

      <BinomIntegrationsSection campaignId={campaignId} />
      <BinomUsersSection campaignId={campaignId} />

      <section className="space-y-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Notes
        </h4>
        {/* Binom's public API has no notes field — edits were silently
            dropped on save, so the field is read-only until the API
            supports it. */}
        <textarea
          value={meta.notes ?? ''}
          disabled
          rows={4}
          title="Not supported by Binom public API"
          placeholder="Not supported by Binom public API."
          aria-label="Notes (not supported by Binom public API)"
          className="w-full cursor-not-allowed rounded border border-input bg-muted/30 p-2 text-xs text-muted-foreground"
        />
      </section>
    </div>
  );
};

export default BinomAdvancedConfigTab;
